import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, isEqual, getDay } from 'date-fns';
import { scheduleAPI } from '../services/api.ts';
import { usePopup } from '../context/PopupContext.tsx';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import MapPicker from '../components/MapPicker.tsx';
import { API_URL, ULTRAPOS_ORIGIN } from '../config/constants.ts';

interface ScheduleItem {
  id: string;
  date: string;
  time: string;
  location: string;
  address: string;
  status?: 'pending' | 'completed' | 'canceled';
  projectName?: string;
  customerName?: string;
  contactNo?: string;
  workFlowStatusId?: number; // 0=Pending, 1=Progress, 2=Completed (note: capital F and S)
  startTime?: string | null;
  imageUrl?: string; // Optional check-in image to display in card
  // HR events
  title?: string;
  clockIn?: string | null;
  clockOut?: string | null;
  totalHour?: number;
  remarks?: string | null;
  jobApplicationId?: number | null;
  profileLoginId?: string | null;
  attachments?: ScheduleAttachment[];
}

interface ScheduleAttachment {
  label?: string;
  fileName?: string;
  viewUrl?: string;
  downloadUrl?: string;
}

// Mock data for demonstration
const mockSchedules: ScheduleItem[] = [
  
];

// Add CheckInData interface similar to Dashboard
interface CheckInData {
  scheduleId: string;
  image: File | null;
  latitude: number | null;
  longitude: number | null;
}

// Add this helper function to format WhatsApp numbers
const formatWhatsAppNumber = (phone: string): string => {
  // Remove all non-numeric characters
  const cleaned = phone.replace(/[^0-9]/g, '');
  
  // If the number already starts with '6' (Malaysia country code), keep it as is
  // Otherwise, prepend '6' to ensure proper WhatsApp link formatting
  return cleaned.startsWith('6') ? cleaned : `6${cleaned}`;
};

// Safely format a time string to HH:mm
const formatTime = (isoString?: string | null): string | null => {
  if (!isoString) return null;
  const d = new Date(isoString);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
};

const buildUltraPosUrl = (url?: string): string => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  const origin = ULTRAPOS_ORIGIN.replace(/\/$/, '');
  const path = url.startsWith('/') ? url : `/${url}`;
  return `${origin}${path}`;
};

const buildScheduleAttachmentUrl = (url?: string): { url: string; requiresAuth: boolean } => {
  if (!url) return { url: '', requiresAuth: false };
  if (/^https?:\/\//i.test(url)) return { url, requiresAuth: false };
  if (url.toLowerCase().startsWith('/approval/')) {
    return { url: buildUltraPosUrl(url), requiresAuth: false };
  }
  const origin = API_URL.replace(/\/$/, '');
  const path = url.startsWith('/') ? url : `/${url}`;
  return { url: `${origin}${path}`, requiresAuth: true };
};

const isImageAttachment = (fileName?: string): boolean =>
  /\.(jpe?g|png|gif|webp|bmp)$/i.test(fileName || '');

const normalizeScheduleAttachments = (item: any): ScheduleAttachment[] => {
  const raw = item.attachments ?? item.Attachments;
  if (!Array.isArray(raw)) return [];

  return raw
    .map((attachment: any) => ({
      label: attachment.label ?? attachment.Label ?? '',
      fileName: attachment.fileName ?? attachment.FileName ?? '',
      viewUrl: attachment.viewUrl ?? attachment.ViewUrl ?? '',
      downloadUrl: attachment.downloadUrl ?? attachment.DownloadUrl ?? ''
    }))
    .filter((attachment: ScheduleAttachment) => attachment.viewUrl || attachment.downloadUrl);
};

const Schedule: React.FC = () => {
  const navigate = useNavigate();
  const { showError, showSuccess } = usePopup();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [schedules, setSchedules] = useState<ScheduleItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(true); // Flag to track if more data is available
  const [isLoadingMore, setIsLoadingMore] = useState(false); // Separate loading state for pagination
  const pageSize = 3; // Fixed page size of 3 items
  
  const scrollRef = useRef<HTMLDivElement>(null); // Ref for the scrollable container
  
  // Add new state variables for the image upload modal
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [checkInData, setCheckInData] = useState<CheckInData>({
    scheduleId: '',
    image: null,
    latitude: null,
    longitude: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [imagePreviewSrc, setImagePreviewSrc] = useState<string | null>(null);
  
  // Add the MapPicker state variables
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState('');
  const previewObjectUrlRef = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      if (previewObjectUrlRef.current) {
        URL.revokeObjectURL(previewObjectUrlRef.current);
      }
    };
  }, []);
  
  // Format date for API calls
  const formatDateForApi = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Initial data load and pagination reset
  useEffect(() => {
    setPageNumber(1);
    setHasMore(true);
    setSchedules([]);
  }, [selectedDate, currentMonth]);

  // Fetch schedules when pageNumber changes
  useEffect(() => {
    fetchSchedules(pageNumber > 1, pageNumber);
  }, [pageNumber, selectedDate, currentMonth]);

  // Set up scroll event listener
  useEffect(() => {
    const handleScroll = () => {
      const scrollContainer = scrollRef.current;
      if (scrollContainer && !loading && !isLoadingMore && hasMore) {
        const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
        if (scrollTop + clientHeight >= scrollHeight - 100) {
          loadMore();
        }
      }
    };
    const scrollContainer = scrollRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, [loading, isLoadingMore, hasMore]);

  // Load more data by incrementing page number
  const loadMore = () => {
    if (!hasMore || isLoadingMore) return;
    setIsLoadingMore(true);
    setPageNumber(prevPage => prevPage + 1);
  };
  
  // Fetch schedules from API
  const fetchSchedules = async (isLoadingMore = false, customPageNumber = 1) => {
    if (!isLoadingMore) {
      setLoading(true);
    }
    try {
      let startDate, endDate;
      if (selectedDate) {
        startDate = formatDateForApi(selectedDate);
        endDate = formatDateForApi(selectedDate);
      } else {
        const today = new Date();
        startDate = formatDateForApi(today);
        endDate = formatDateForApi(today);
        if (currentMonth.getMonth() !== today.getMonth() || currentMonth.getFullYear() !== today.getFullYear()) {
          const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
          const lastDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
          startDate = formatDateForApi(firstDay);
          endDate = formatDateForApi(lastDay);
        }
      }
      console.log('Fetching schedules with date range:', { startDate, endDate });
      const response = await scheduleAPI.getUpcomingServices({
        PageNumber: customPageNumber,
        PageSize: pageSize,
        StartDate: startDate,
        EndDate: endDate
      });
      const newSchedules = (response.data || []).map((item: any, idx: number) => {
        const clockInRaw = item.clockIn ?? item.ClockIn ?? null;
        const clockOutRaw = item.clockOut ?? item.ClockOut ?? null;
        const clockInDate = clockInRaw ? new Date(clockInRaw as string) : null;
        const validClock = clockInDate && !isNaN(clockInDate.getTime()) ? clockInDate : null;
        const jaId = item.jobApplicationId ?? item.JobApplicationId;
        const profileLogin = item.profileLoginId ?? item.ProfileLoginId ?? null;
        const pad2 = (n: number) => String(n).padStart(2, '0');
        const localYmd = validClock
          ? `${validClock.getFullYear()}-${pad2(validClock.getMonth() + 1)}-${pad2(validClock.getDate())}`
          : '';
        const localHm = validClock
          ? validClock.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
          : '';
        return {
          ...item,
          clockIn: clockInRaw,
          clockOut: clockOutRaw,
          id:
            item.id ??
            item.Id ??
            (jaId != null && clockInRaw
              ? `ja-${jaId}-${clockInRaw}`
              : `row-${idx}-${validClock?.getTime() ?? idx}`),
          date: validClock ? localYmd : item.date || item.Date || '',
          time: validClock ? localHm : item.time || item.Time || '',
          remarks: item.remarks || item.Remarks || null,
          address: item.address || item.Address || null,
          jobApplicationId: jaId ?? null,
          profileLoginId: profileLogin && String(profileLogin).trim() ? String(profileLogin).trim() : null,
          contactNo: (item.contactNo ?? item.ContactNo ?? '').toString().trim() || undefined,
          attachments: normalizeScheduleAttachments(item)
        };
      });
      if (isLoadingMore) {
        setSchedules(prev => [...prev, ...newSchedules]);
      } else {
        setSchedules(newSchedules);
      }
      if (newSchedules.length < pageSize) {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error fetching schedules:', error);
      showError('Failed to load schedules. Please try again.');
      if (isLoadingMore) {
        const start = (customPageNumber - 1) * pageSize;
        const end = customPageNumber * pageSize;
        const moreMockData = mockSchedules.slice(start, end);
        if (moreMockData.length > 0) {
          setSchedules(prev => [...prev, ...moreMockData]);
        }
      } else {
        setSchedules([]);
      }
    } finally {
      if (!isLoadingMore) {
        setLoading(false);
      } else {
        setIsLoadingMore(false);
      }
    }
  };

  // Previous and next month handlers
  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
    setSelectedDate(null);
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
    setSelectedDate(null);
  };

  // Handle date selection
  const handleDateSelect = (date: Date) => {
    // Toggle selection if clicking the same date
    if (selectedDate && 
        date.getDate() === selectedDate.getDate() && 
        date.getMonth() === selectedDate.getMonth() && 
        date.getFullYear() === selectedDate.getFullYear()) {
      setSelectedDate(null);
    } else {
      setSelectedDate(date);
    }
  };

  // Get days in current month
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Event dots
  const [eventDays, setEventDays] = useState<Set<string>>(new Set());
  useEffect(() => {
    const startStr = formatDateForApi(monthStart);
    const endStr = formatDateForApi(monthEnd);
    scheduleAPI.getEventDays({ StartDate: startStr, EndDate: endStr })
      .then((rows: any[]) => {
        const set = new Set<string>();
        (rows || []).forEach((r: any) => {
          const d = r.date || r.Date || r.createdDate || r.CreatedDateTime;
          if (d) set.add(String(d).substring(0,10));
        });
        setEventDays(set);
      })
      .catch(() => setEventDays(new Set()));
  }, [currentMonth]);

  // Calculate empty cells for the days before the first of the month
  const firstDayOfMonth = getDay(monthStart);  // This returns 0 for Sunday, 1 for Monday, etc.
  const emptyDays = Array.from({ length: firstDayOfMonth }, (_, i) => i);
  
  // Helper functions to check schedule status
  const isScheduleCompleted = (schedule: ScheduleItem): boolean => {
    return schedule.workFlowStatusId === 2; // 2 = Completed
  };

  const isScheduleInProgress = (schedule: ScheduleItem): boolean => {
    return schedule.workFlowStatusId === 1; // 1 = Progress
  };

  const canCheckIn = (schedule: ScheduleItem): boolean => {
    // Can check-in if it's pending and not already started
    return schedule.workFlowStatusId === 0 && !schedule.startTime;
  };

  // Update handleCheckIn to show modal instead of direct API call
  const handleCheckIn = async (e: React.MouseEvent, scheduleId: string) => {
    e.preventDefault();
    initiateCheckIn(scheduleId);
  };
  
  // Add initiateCheckIn function
  const initiateCheckIn = (scheduleId: string) => {
    setCheckInData({
      scheduleId,
      image: null,
      latitude: null,
      longitude: null,
    });
    setShowCheckInModal(true);
    getCurrentLocation(); // Start getting location immediately
  };
  
  // Add getCurrentLocation function
  const getCurrentLocation = () => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCheckInData(prev => ({
            ...prev,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          }));
        },
        (error) => {
          console.error('Error getting location:', error);
          toast.error('Unable to get your location. Please enable location services.');
        }
      );
    } else {
      toast.error('Geolocation is not supported by your browser');
    }
  };
  
  // Add handleImageChange function
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      // Store the raw File object
      const file = e.target.files[0];
      console.log('Selected file:', file.name, file.type, `${Math.round(file.size / 1024)} KB`);
      setCheckInData(prev => ({
        ...prev,
        image: file
      }));
    }
  };
  
  // Add handleSubmitCheckIn function
  const handleSubmitCheckIn = async () => {
    if (!checkInData.image) {
      toast.error('Please upload an image');
      return;
    }

    if (!checkInData.latitude || !checkInData.longitude) {
      toast.error('Location data is required. Please enable location services.');
      return;
    }

    setIsSubmitting(true);
    try {
      console.log('Starting check-in for schedule:', checkInData.scheduleId);
      
      // Call the API with the file as binary data
      await scheduleAPI.checkIn(
        Number(checkInData.scheduleId), 
        checkInData.image,
        checkInData.latitude.toString(),
        checkInData.longitude.toString()
      );
      
      // Show success toast and close modal
      toast.success('Successfully checked in! Redirecting to details page...');
      setShowCheckInModal(false);
      
      // Wait for 1.5 seconds to show the toast before navigating
      setTimeout(() => {
        // Navigate to schedule detail page
        navigate(`/schedule/${checkInData.scheduleId}`);
      }, 1500);
      
    } catch (error: any) {
      console.error('Error checking in:', error);
      
      let errorMessage = error.message || 'Failed to check in. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Function to handle address click
  const handleAddressClick = (address: string) => {
    setSelectedAddress(address);
    setShowMapPicker(true);
  };

  const openScheduleAttachment = async (attachment: ScheduleAttachment) => {
    const target = buildScheduleAttachmentUrl(attachment.viewUrl || attachment.downloadUrl);
    if (!target.url) return;

    const canPreviewImage = isImageAttachment(attachment.fileName);
    if (!target.requiresAuth) {
      if (canPreviewImage) {
        setImagePreviewSrc(target.url);
      } else {
        window.open(target.url, '_blank', 'noopener,noreferrer');
      }
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(target.url, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const blob = await response.blob();
      if (previewObjectUrlRef.current) {
        URL.revokeObjectURL(previewObjectUrlRef.current);
      }
      const objectUrl = URL.createObjectURL(blob);
      previewObjectUrlRef.current = objectUrl;

      if (canPreviewImage || blob.type.startsWith('image/')) {
        setImagePreviewSrc(objectUrl);
        return;
      }

      const opened = window.open(objectUrl, '_blank', 'noopener,noreferrer');
      if (!opened) {
        window.location.href = objectUrl;
      }
    } catch (error) {
      console.error('Error opening schedule attachment:', error);
      toast.error('Failed to open attachment. Please try again.');
    }
  };

  return (
    <div className="container-app py-6" ref={scrollRef} style={{ height: '100vh', overflowY: 'auto' }}>
      <ToastContainer position="top-right" autoClose={3000} />
      <h1 className="text-2xl font-bold mb-6">My Calander</h1>

      {/* Calendar View */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 border-b flex justify-between items-center">
          <button 
            onClick={prevMonth}
            className="p-2 rounded-full hover:bg-gray-200"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h2 className="text-lg font-medium">{format(currentMonth, 'MMMM yyyy')}</h2>
          <button 
            onClick={nextMonth}
            className="p-2 rounded-full hover:bg-gray-200"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
        <div className="p-4">
          {/* Day headers */}
          <div className="grid grid-cols-7 mb-2">
            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
              <div key={i} className="text-center font-medium text-sm">
                {day}
              </div>
            ))}
          </div>
          
          {/* Days of the month */}
          <div className="grid grid-cols-7 gap-1">
            {emptyDays.map((_, i) => (
              <div key={i} className="h-10 rounded-full flex items-center justify-center"></div>
            ))}
            {daysInMonth.map((day, i) => {
              // Find if the day has any scheduled items
              const hasSchedule = schedules.some(schedule => {
                try {
                  if (!schedule.date) return false;
                  const scheduleDate = new Date(schedule.date);
                  // Check if the date is valid before comparing
                  if (isNaN(scheduleDate.getTime())) return false;
                  
                  // Use strict date comparison to ensure exact day match
                  return scheduleDate.getDate() === day.getDate() && 
                         scheduleDate.getMonth() === day.getMonth() &&
                         scheduleDate.getFullYear() === day.getFullYear();
                } catch {
                  return false;
                }
              });
              
              // Check if day is selected
              const isSelected = selectedDate && 
                                 day.getDate() === selectedDate.getDate() && 
                                 day.getMonth() === selectedDate.getMonth() && 
                                 day.getFullYear() === selectedDate.getFullYear();
              
              const isoDay = formatDateForApi(day);
              const showDot = eventDays.has(isoDay);
              return (
                <button
                  key={i}
                  className={`
                    h-10 rounded-full flex items-center justify-center
                    ${isSelected 
                      ? 'bg-blue-500 text-white' 
                      : ''}
                  `}
                  onClick={() => handleDateSelect(day)}
                >
                  <div className="flex flex-col items-center leading-none">
                    <span className="text-sm">{format(day, 'd')}</span>
                    {!isSelected && showDot && <span className="mt-0.5 w-1.5 h-1.5 rounded-full bg-primary"></span>}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Schedule List */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold mb-2">
          {selectedDate 
            ? `Schedules for ${format(selectedDate, 'MMMM d, yyyy')}` 
            : `Schedules for ${format(currentMonth, 'MMMM yyyy')}`}
        </h2>
        
        {loading && pageNumber === 1 ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
            <p className="mt-2 text-gray-600">Loading schedules...</p>
          </div>
        ) : schedules.length > 0 ? (
          <>
            {schedules.map((schedule, index) => (
              <div 
                key={`${schedule.id}-${index}`}
                className="block bg-white rounded-lg shadow hover:shadow-md transition-shadow"
              >
                <div className="p-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="flex items-center mb-2">
                        <div className="bg-primary text-white p-1 rounded mr-2">
                          {(() => {
                            try {
                              if (!schedule.date) return <span className="text-xs font-bold">N/A</span>;
                              const date = new Date(schedule.date);
                              if (isNaN(date.getTime())) return <span className="text-xs font-bold">N/A</span>;
                              // Format to match dashboard: "14 Apr 2025"
                              return (
                                <span className="text-xs font-bold">
                                  {date.getDate()} {date.toLocaleString('default', { month: 'short' })} {date.getFullYear()}
                                </span>
                              );
                            } catch {
                              return <span className="text-xs font-bold">N/A</span>;
                            }
                          })()}
                        </div>
                        <span className="text-sm text-gray-600">{schedule.time || 'No time specified'}</span>
                      </div>
                    </div>
                    
                    {/* status badge removed as requested */}
                  </div>
                  
                  <div className="mt-2">
                    {/* Prefer HR event title when present; otherwise fallback to project/customer */}
                    {schedule.title && (
                      <h3 className="font-bold text-primary mb-1">{schedule.title}</h3>
                    )}
                    {!schedule.title && schedule.projectName && (
                      <h3 className="font-bold text-primary mb-1">{schedule.projectName}</h3>
                    )}
                    {!schedule.title && schedule.customerName && (
                      <p className="text-sm text-gray-700 font-medium mb-1">{schedule.customerName}</p>
                    )}

                    {/* Clock times when available */}
                    {(schedule.clockIn || schedule.clockOut) && (
                      <div className="text-sm text-gray-700 mb-1">
                        {schedule.clockIn && <span>In: {formatTime(schedule.clockIn)}</span>}
                        {schedule.clockIn && schedule.clockOut && <span className="mx-2">·</span>}
                        {schedule.clockOut && <span>Out: {formatTime(schedule.clockOut)}</span>}
                      </div>
                    )}
                  {schedule.imageUrl && (
                    <div className="mt-2">
                      <img
                        src={schedule.imageUrl}
                        alt="Check-in"
                        className="w-20 h-20 object-cover rounded cursor-zoom-in border"
                        onClick={() => setImagePreviewSrc(schedule.imageUrl!)}
                      />
                    </div>
                  )}
                    {schedule.attachments && schedule.attachments.length > 0 && (
                      <div className="mt-2 mb-2 flex flex-wrap gap-2">
                        {schedule.attachments.map((attachment, attachmentIndex) => {
                          const canPreviewImage = isImageAttachment(attachment.fileName);
                          return (
                            <button
                              key={`${attachment.fileName || attachment.label || 'attachment'}-${attachmentIndex}`}
                              type="button"
                              onClick={() => openScheduleAttachment(attachment)}
                              className="inline-flex max-w-full items-center gap-1 rounded border border-gray-200 bg-gray-50 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100"
                              title={attachment.fileName || attachment.label || 'Attachment'}
                            >
                              <svg className="h-3.5 w-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                {canPreviewImage ? (
                                  <>
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                  </>
                                ) : (
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.586-6.586a4 4 0 00-5.657-5.657L5.757 10.757a6 6 0 108.486 8.486L20.5 13" />
                                )}
                              </svg>
                              <span className="truncate">
                                {attachment.label || attachment.fileName || 'Attachment'}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {schedule.jobApplicationId != null && schedule.jobApplicationId > 0 && (
                      <div className="mb-2">
                        <a
                          href={`${ULTRAPOS_ORIGIN.replace(/\/$/, '')}/JobApplication/Index?openView=${encodeURIComponent(String(schedule.jobApplicationId))}#managementTab`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-primary font-medium underline-offset-2 hover:underline"
                        >
                          Open in Ultra POS (View application)
                        </a>
                      </div>
                    )}
                    {schedule.jobApplicationId != null &&
                      schedule.jobApplicationId > 0 &&
                      schedule.contactNo && (
                      <div className="mb-1">
                        <p className="text-[11px] uppercase tracking-wide text-gray-500 mb-0.5">Contact (WhatsApp)</p>
                        <a
                          href={`https://wa.me/${formatWhatsAppNumber(schedule.contactNo)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-green-600 font-medium flex items-center"
                        >
                          <svg
                            className="w-3 h-3 mr-1 shrink-0 text-green-600"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                          </svg>
                          <span className="text-green-600">{schedule.contactNo}</span>
                        </a>
                      </div>
                    )}
                    {/* Display address or remarks (rich text) if address is empty */}
                    {schedule.address ? (
                      <p 
                        className="text-xs text-gray-900 font-medium cursor-pointer hover:underline"
                        onClick={() => handleAddressClick(schedule.address)}
                      >
                        {schedule.address}
                      </p>
                    ) : schedule.remarks ? (
                      <div 
                        className="text-xs text-gray-900 schedule-remarks"
                        dangerouslySetInnerHTML={{ __html: schedule.remarks }}
                      />
                    ) : null}
                  </div>
                  
                  <div className="mt-2">
                    <div className="flex justify-end items-center mt-3 space-x-2">
                      {canCheckIn(schedule) && (
                        <button
                          onClick={(e) => handleCheckIn(e, schedule.id)}
                          className="bg-primary text-white px-4 py-2 rounded text-sm font-medium flex items-center"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                          Check In
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            
            {isLoadingMore && (
              <div className="p-4 text-center">
                <div className="inline-block animate-spin h-6 w-6 border-4 border-primary border-t-transparent rounded-full"></div>
                <p className="mt-2 text-sm text-gray-600">Loading more...</p>
              </div>
            )}
            
            {!hasMore && !isLoadingMore && schedules.length > 0 && (
              <div className="p-4 text-center">
                <p className="text-sm text-gray-600">No more schedules to load</p>
              </div>
            )}
          </>
        ) : (
          <div className="p-8 text-center bg-white rounded-lg shadow">
            <svg className="w-16 h-16 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="mt-4 text-gray-600">No schedules found for this period</p>
          </div>
        )}
      </div>

      {/* Check-in Modal */}
      {showCheckInModal && (
        <div className="fixed inset-0 z-50 flex flex-col justify-end items-center bg-black bg-opacity-70 transition-all duration-300 animate-fadeIn pb-safe">
          <div className="w-full bg-white rounded-t-2xl shadow-xl overflow-hidden transform transition-transform duration-300 animate-slideUp">
            <div className="relative bg-primary py-4 px-4">
              <h2 className="text-xl font-bold text-white text-center">Check In</h2>
              <button 
                onClick={() => setShowCheckInModal(false)}
                className="absolute right-4 top-4 text-white"
                aria-label="Close modal"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-4">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Image <span className="text-red-500">*</span>
                </label>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/jpg,image/heic"
                  onChange={handleImageChange}
                  ref={fileInputRef}
                  className="hidden"
                />
                
                <div 
                  className={`relative border-2 ${checkInData.image ? 'border-primary' : 'border-dashed border-gray-300'} rounded-lg overflow-hidden`}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {checkInData.image ? (
                    <div className="relative">
                      <img
                        src={URL.createObjectURL(checkInData.image)}
                        alt="Preview"
                        className="w-full h-48 object-cover"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white px-2 py-1 text-xs">
                        {checkInData.image.name} ({Math.round(checkInData.image.size / 1024)} KB)
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setCheckInData(prev => ({ ...prev, image: null }));
                        }}
                        className="absolute top-2 right-2 bg-white rounded-full p-1 shadow-md text-red-500"
                        aria-label="Remove image"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-6 px-4 text-center">
                      <svg className="w-10 h-10 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p className="text-gray-600 font-medium">Tap to take a photo</p>
                      <p className="text-xs text-gray-500 mt-1">JPG, PNG, or HEIC</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center mb-5">
                <div className="mr-2">
                  <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <div className="text-sm text-gray-700">Location Status</div>
                  {checkInData.latitude && checkInData.longitude ? (
                    <div className="text-sm text-green-600">
                      ✓ Location captured
                    </div>
                  ) : (
                    <div className="text-sm text-yellow-600">
                      Capturing location...
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 p-4">
              <div className="grid grid-cols-1 gap-3">
                <button
                  onClick={handleSubmitCheckIn}
                  disabled={isSubmitting || !checkInData.image || !checkInData.latitude || !checkInData.longitude}
                  className={`w-full py-3 rounded-md text-white font-medium text-center ${
                    isSubmitting || !checkInData.image || !checkInData.latitude || !checkInData.longitude
                      ? 'bg-gray-400'
                      : 'bg-primary'
                  }`}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Check-in'}
                </button>
                
                <button
                  onClick={() => setShowCheckInModal(false)}
                  className="w-full py-3 border border-gray-300 rounded-md text-gray-700 font-medium text-center bg-white"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image preview modal */}
      {imagePreviewSrc && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center" onClick={() => setImagePreviewSrc(null)}>
          <img src={imagePreviewSrc} alt="Preview" className="max-w-full max-h-full" />
        </div>
      )}

      {showMapPicker && (
        <MapPicker
          isOpen={showMapPicker}
          onClose={() => setShowMapPicker(false)}
          address={selectedAddress}
        />
      )}
    </div>
  );
};

export default Schedule; 
